<template>
  <section>
    <div class="border-b flex justify-between items-center pb-3">
      <div
        class="flex-col lg:flex-row items-center mb-4 lg:mb-0 overflow-x-auto"
      >
        <button
          v-for="(category, index) in transactionCategories"
          :key="index"
          @click="setActiveCategory(category)"
        >
          <div
            :class="[
              category.isActive
                ? 'border border-primary'
                : ' border-main hover-animation-accent-subtle'
            ]"
            class="text-fg-muted font-normal text-9px lg:text-13px mr-1 flex py-1 px-2 items-center rounded-lg whitespace-nowrap"
          >
            <p
              class="mr-1 py-1 capitalize"
              :class="{ 'text-primary': category.isActive }"
            >
              {{ category.name }}
            </p>
            <p
              v-if="!isLoading"
              :class="[
                category.isActive ? 'text-primary bg-secondary-2' : 'bg-gray-30'
              ]"
              class="py-1 px-2.5 rounded-2xl"
            >
              {{ category.count || 0 }}
            </p>
          </div>
        </button>
      </div>
      <div class="hidden lg:flex lg:ml-auto">
        <div class="flex gap-x-3 items-center ml-auto">
          <div>
            <div class="relative w-full flex items-center justify-end">
              <div>
                <button
                  @click="toggleDropdown"
                  class="flex items-center pl-2 pr-3 md:pl-4 md:pr-5 py-2 lg:py-1.5 rounded-lg border"
                >
                  <currency-icon
                    :currency="getCurrencyCountry(filter.currency)"
                  />
                  <p class="hidden xl:block mx-1.5 text-13px font-normal">
                    {{ filter.currency || 'Currency' }}
                  </p>
                  <arrow-down-icon />
                </button>
              </div>
              <!-- Options for the dropdown -->
              <div
                v-if="dropdownIsVisible"
                test-id="dropdown"
                class="absolute top-12 bg-white border rounded-lg shadow-2xl px-2 py-3 h-48 w-full overflow-scroll z-10"
              >
                <div
                  v-for="(
                    currency, index
                  ) in supportedExternalApiCurrencyOptions"
                  :key="index"
                  class="cursor-pointer"
                >
                  <div
                    @click="setCurrencyFilter(currency.currency)"
                    :class="{
                      'bg-muted': filter.currency === currency.currency
                    }"
                    class="hover:bg-bg-muted rounded-lg flex items-center mb-2 px-3 py-2 font-normal text-sm"
                  >
                    <currency-icon
                      :currency="getCurrencyCountry(currency.currency)"
                    />
                    <p class="ml-1.5">{{ currency.currency }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            class="flex items-center gap-x-1 border border-root-3 text-root-2 px-3.5 xl:px-2.5 xl:py-1.5 lg:py-2 py-2.5 rounded-lg cursor-pointer"
            @click="showDialog('showTableFilter')"
          >
            <filter-icon />
            <p
              class="hidden xl:inline-block text-black-2 text-sm px-2 font-normal"
            >
              Filter
            </p>
            <div class="hidden xl:inline-block">
              <arrow-down-icon />
            </div>
          </div>
          <div class="flex items-center">
            <tooltip placement="top" :content="refreshToolTipContent">
              <div class="cursor-pointer" @click="resetFilter">
                <refresh-icon />
              </div>
            </tooltip>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-6">
      <customer-transaction-table
        v-if="activeCategory === this.$transactionType.TRANSFER"
        :tableData="transferCustomerTransactions"
        :page="transferPage"
        :isLoading="isLoading"
        :transactionType="transactionType"
      />
      <customer-transaction-table
        v-if="activeCategory === this.$transactionType.WITHDRAWAL"
        :tableData="withdrawalCustomerTransactions"
        :page="withdrawalPage"
        :isLoading="isLoading"
        :transactionType="transactionType"
      />
      <customer-transaction-table
        v-if="activeCategory === this.$transactionType.DEPOSIT"
        :tableData="depositCustomerTransactions"
        :page="depositPage"
        :isLoading="isLoading"
        :transactionType="transactionType"
      />
    </div>
    <filter-dialog
      :showDialog="showTableFilter"
      @filterParams="searchFilterParams"
      @closeDialog="closeDialog('showTableFilter')"
      @cancel="handleCancel"
    />
  </section>
</template>
<script>
import { GetCurrencyCountry } from '@/utils/helpers'
import applyDateFilter from '@/mixins/app.js'
import { SUPPORTED_EXTERNAL_API_CURRENCY_OPTIONS } from '@/utils/constants'
export default {
  mixins: [applyDateFilter],
  data () {
    return {
      defaultPayload: null,
      supportedExternalApiCurrencyOptions:
        SUPPORTED_EXTERNAL_API_CURRENCY_OPTIONS,
      refreshToolTipContent: 'Reset filter settings to default',
      dropdownIsVisible: false,
      showTableFilter: false,
      filter: {
        currency: ''
      },
      isLoading: false,
      activeCategory: this.$transactionType.TRANSFER,
      transactionType: this.$transactionType.TRANSFER,
      payload: {
        page: 1,
        size: 10,
        customerId: '',
        currency: ''
      }
    }
  },
  components: {
    CustomerTransactionTable: () =>
      import('@/components/v2/externalApi/tables/CustomerTransactionTable.vue'),
    ArrowDownIcon: () => import('@/assets/v2/icons/ArrowDownTwo.vue'),
    FilterIcon: () => import('@/assets/v2/icons/FilterIcon.vue'),
    CurrencyIcon: () => import('@/assets/icons/CurrencyIcons.vue'),
    RefreshIcon: () => import('@/assets/v2/icons/RefreshIcon.vue'),
    FilterDialog: () =>
      import('@/components/v2/bulkPayout/dialogs/FilterDialog.vue')
  },
  methods: {
    resetFilter () {
      // reset all filters
      this.payload = {
        page: 1,
        size: 10,
        customerId: '',
        currency: ''
      }
      this.filter = {
        currency: 'Currency'
      }
      this.getAllCustomerDetailsTransaction()
    },
    setCurrencyFilter (currency) {
      this.filter.currency = currency
      this.dropdownIsVisible = false
      this.handleCurrency()
    },
    async handleCurrency () {
      this.payload.currency = this.filter.currency
      this.getAllCustomerDetailsTransaction()
    },
    searchFilterParams (filterParams) {
      this.applyDateFilter(filterParams)
      this.getAllCustomerDetailsTransaction()
    },
    handleCancel () {
      this.payload = this.defaultPayload
    },
    showDialog (dialog) {
      this[dialog] = true
    },
    closeDialog (dialog) {
      this[dialog] = false
    },
    toggleDropdown () {
      this.dropdownIsVisible = !this.dropdownIsVisible
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    setActiveCategory (tab) {
      this.activeCategory = tab.name
      this.transactionType = this.activeCategory
      this.updateActiveTransactionType()
    },
    async getCustomerDetailsTransactions (type) {
      const customer_Id = this.$route.params.id
      this.payload.customerId = customer_Id
      this.isLoading = true
      const vm = this
      await this.$store
        .dispatch(`externalApi/get${type}CustomerDetailsTransactions`, {
          ...this.payload,
          type
        })
        .then(() => {
          this.isLoading = false
        })
        .catch((error) => {
          this.isLoading = false
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    getAllCustomerDetailsTransaction () {
      this.getCustomerDetailsTransactions('Transfer')
      this.getCustomerDetailsTransactions('Deposit')
      this.getCustomerDetailsTransactions('Withdrawal')
    },
    async updateActiveTransactionType () {
      this.transactionCategories.forEach((category) => {
        if (category.name === this.activeCategory) {
          category.isActive = true
        } else {
          category.isActive = false
        }
      })
    }
  },
  computed: {
    transferCustomerTransactions () {
      return (
        this.$store.state.externalApi.transferCustomerDetailsTransactions
          .items || []
      )
    },
    transferPage () {
      return (
        this.$store.state.externalApi.transferCustomerDetailsTransactions
          .page || {}
      )
    },
    withdrawalCustomerTransactions () {
      return (
        this.$store.state.externalApi.withdrawalCustomerDetailsTransactions
          .items || []
      )
    },
    withdrawalPage () {
      return (
        this.$store.state.externalApi.withdrawalCustomerDetailsTransactions
          .page || {}
      )
    },
    depositCustomerTransactions () {
      return (
        this.$store.state.externalApi.depositCustomerDetailsTransactions
          .items || []
      )
    },
    depositPage () {
      return (
        this.$store.state.externalApi.depositCustomerDetailsTransactions.page ||
        {}
      )
    },
    transactionCategories () {
      return [
        {
          name: this.$transactionType.TRANSFER,
          isActive: true,
          count: this.transferPage.totalCount
        },
        {
          name: this.$transactionType.WITHDRAWAL,
          isActive: false,
          count: this.withdrawalPage.totalCount
        },
        {
          name: this.$transactionType.DEPOSIT,
          isActive: false,
          count: this.depositPage.totalCount
        }
      ]
    }
  },
  mounted () {
    this.getAllCustomerDetailsTransaction()
  }
}
</script>
<style scoped>
>>> .el-tooltip__popper.is-dark,
>>> .el-tooltip__popper.is-light,
>>> .el-tooltip__popper {
  background: white !important;
}

>>> .el-table {
  padding: 0;
  background: transparent;
  border-collapse: separate;
}

>>> .el-table table {
  border-spacing: 0 16px;
}

>>> .el-table th {
  border: none !important;
  background: #f7f7f7;
  padding: 0;
}

>>> .el-table tr {
  background: white;
  border-radius: 8px;
}
>>> .el-table tr:hover {
  cursor: pointer;
}
>>> .el-table td {
  border-bottom: none !important;
  background: white;
}

>>> .el-table td:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}
>>> .el-table td:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

>>> .el-table::before {
  height: 0px;
}
>>> .el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell {
  background: white !important;
}
>>> .el-pagination.is-background .btn-next.disabled,
.el-pagination.is-background .btn-next:disabled,
.el-pagination.is-background .btn-prev.disabled,
.el-pagination.is-background .btn-prev:disabled,
.el-pagination.is-background .el-pager li.disabled {
  background: green !important;
}
>>> .el-pagination.is-background .btn-next,
.el-pagination.is-background .btn-prev,
.el-pagination.is-background .el-pager li {
  background: transparent !important;
  border: 0.941331px solid rgba(207, 219, 213, 0.7);
  border-radius: 7.53065px;
}
>>> .el-pagination.is-background .btn-prev,
.el-pagination.is-background .btn-next,
.el-pagination.is-background .el-pager li {
  background: transparent !important;
  border: 0.941331px solid rgba(207, 219, 213, 0.7);
  border-radius: 7.53065px;
}
>>> li.number.active {
  background-color: var(--color-primary) !important;
  border: none !important;
  color: white !important;
  border-radius: 7.53065px !important;
}
>>> li.number {
  border: none !important;
  font-weight: 500;
  font-size: 12px;
  color: var(--text-2);
  background: transparent !important;
  /* color: var(--color-primary) !important ; */
}
>>> li.number:hover {
  border: 0.941331px solid var(--color-primary) !important;
  border-radius: 7.53065px !important;
  color: var(--text-2) !important;
}
>>> .el-input--prefix .el-input__inner {
  border: 1px solid var(--border-1);
  border-radius: 8px;
  background: var(--background-10);
}
>>> .el-tooltip__popper.is-light {
  background: white !important;
}
>>> .el-input__inner {
  height: 40px;
  border-radius: 8px;
}
</style>
